/**
 * Offer Template Types
 * Çok sayfalı blok tabanlı teklif PDF şablon sistemi
 */

// ============================================================================
// BLOK TİPLERİ
// ============================================================================

export type ContentBlockType =
  | 'heading'
  | 'paragraph'
  | 'quote'
  | 'statGrid'
  | 'serviceGrid'
  | 'image'
  | 'divider'
  | 'spacer';

export type OfferBlockType =
  | 'greeting'
  | 'customerInfo'
  | 'itemsTable'
  | 'totals'
  | 'terms'
  | 'bankAccounts'
  | 'signature'
  | 'footer';

export type BlockType = ContentBlockType | OfferBlockType;

// ============================================================================
// SAYFA TİPLERİ
// ============================================================================

export type PageType = 'coverPage' | 'contentPage' | 'offerPage';

// ============================================================================
// BLOK AYARLARI (Settings)
// ============================================================================

export interface HeadingBlockSettings {
  text: string;
  fontSize?: number;
  color?: string;
  align?: 'left' | 'center' | 'right';
}

export interface ParagraphBlockSettings {
  text: string;
  fontSize?: number;
  color?: string;
  align?: 'left' | 'center' | 'right';
}

export interface QuoteBlockSettings {
  text: string;
  bgColor?: string;
  borderColor?: string;
  fontSize?: number;
}

export interface StatGridItem {
  value: string;
  label: string;
}

export interface StatGridBlockSettings {
  items: StatGridItem[];
  columns?: 2 | 3 | 4;
  accentColor?: string;
}

export interface ServiceGridItem {
  title: string;
  icon?: string;
}

export interface ServiceGridBlockSettings {
  items: ServiceGridItem[];
  columns?: 2 | 3;
  accentColor?: string;
}

export interface ImageBlockSettings {
  url: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  caption?: string;
}

export interface DividerBlockSettings {
  color?: string;
  thickness?: number;
  style?: 'solid' | 'dashed' | 'dotted';
}

export interface SpacerBlockSettings {
  height?: number;
}

export interface GreetingBlockSettings {
  text: string;
  showDate?: boolean;
}

export interface CustomerInfoBlockSettings {
  borderStyle?: 'left-accent' | 'full' | 'none';
  showTaxInfo?: boolean;
  showAddress?: boolean;
  showPhone?: boolean;
  showEmail?: boolean;
}

export interface OfferDetailsBlockSettings {
  position?: 'right' | 'left' | 'full';
}

export interface ItemsTableBlockSettings {
  zebraStripes?: boolean;
  headerBgColor?: string;
  showDiscount?: boolean;
  showSct?: boolean;
}

export interface TotalsBlockSettings {
  position?: 'right' | 'center' | 'full';
  showDetails?: boolean;
}

export interface TermsBlockSettings {
  text?: string;
  borderStyle?: 'left-accent' | 'full' | 'none';
  bgColor?: string;
}

export interface BankAccountsBlockSettings {
  showHeader?: boolean;
}

export interface SignatureBlockSettings {
  name?: string;
  title?: string;
  showStamp?: boolean;
}

export interface FooterBlockSettings {
  showMersis?: boolean;
  showTradeRegistry?: boolean;
  showContact?: boolean;
}

export type BlockSettings =
  | HeadingBlockSettings
  | ParagraphBlockSettings
  | QuoteBlockSettings
  | StatGridBlockSettings
  | ServiceGridBlockSettings
  | ImageBlockSettings
  | DividerBlockSettings
  | SpacerBlockSettings
  | GreetingBlockSettings
  | CustomerInfoBlockSettings
  | OfferDetailsBlockSettings
  | ItemsTableBlockSettings
  | TotalsBlockSettings
  | TermsBlockSettings
  | BankAccountsBlockSettings
  | SignatureBlockSettings
  | FooterBlockSettings;

// ============================================================================
// SAYFA AYARLARI
// ============================================================================

export interface CoverPageSettings {
  titleText?: string;
  subtitleText?: string;
  preparedBy?: {
    name?: string;
    email?: string;
    web?: string;
    title?: string;
  };
  showDecorCircles?: boolean;
  bgGradient?: boolean;
}

export interface ContentPageSettings {
  bgColor?: string;
}

export interface OfferPageSettings {
  showGreeting?: boolean;
}

export type PageSettings = CoverPageSettings | ContentPageSettings | OfferPageSettings;

// ============================================================================
// BLOK VE SAYFA YAPISI
// ============================================================================

export interface TemplateBlock {
  id: string;
  type: BlockType;
  visible: boolean;
  order: number;
  settings: Record<string, any>;
}

export interface TemplatePage {
  id: string;
  type: PageType;
  visible: boolean;
  order: number;
  title: string;
  settings: Record<string, any>;
  blocks?: TemplateBlock[];
}

export interface TemplateLayoutConfig {
  pages: TemplatePage[];
}

// ============================================================================
// ANA ŞABLON KONFİGÜRASYONU
// ============================================================================

export interface OfferTemplateConfig {
  id: string;
  companyId: string;
  templateName: string;
  isDefault: boolean;
  slogan?: string;
  logoUrl?: string;
  anaRenk: string;
  aksanRenk: string;
  font: string;
  fontBoyutu: number;
  odemeKosullari?: string;
  gecerlilikNotu?: string;
  altBilgiNotu?: string;
  layoutConfig?: TemplateLayoutConfig;
  createdAt: string;
  updatedAt?: string;
}

// ============================================================================
// VARSAYILAN LAYOUT (Kapak + Teklif sayfası)
// ============================================================================

export const DEFAULT_LAYOUT_CONFIG: TemplateLayoutConfig = {
  pages: [
    {
      id: 'page-cover',
      type: 'coverPage',
      visible: true,
      order: 0,
      title: 'Kapak',
      settings: {
        titleText: 'Proje\nTeklifi',
        subtitleText: '',
        showDecorCircles: true,
        bgGradient: true,
        preparedBy: { name: '', email: '', web: '', title: '' },
      } as CoverPageSettings,
    },
    {
      id: 'page-offer',
      type: 'offerPage',
      visible: true,
      order: 1,
      title: 'Teklif',
      settings: { showGreeting: true } as OfferPageSettings,
      blocks: [
        { id: 'blk-greeting', type: 'greeting', visible: true, order: 0, settings: { text: 'Sunduğumuz çözüm ve hizmetlere ilişkin fiyat teklifimizi bilgilerinize sunarız.', showDate: true } as GreetingBlockSettings },
        { id: 'blk-customer', type: 'customerInfo', visible: true, order: 1, settings: { borderStyle: 'left-accent', showTaxInfo: true, showAddress: true, showPhone: true, showEmail: true } as CustomerInfoBlockSettings },
        { id: 'blk-table', type: 'itemsTable', visible: true, order: 2, settings: { zebraStripes: true, showDiscount: true, showSct: false } as ItemsTableBlockSettings },
        { id: 'blk-totals', type: 'totals', visible: true, order: 3, settings: { position: 'right', showDetails: true } as TotalsBlockSettings },
        { id: 'blk-terms', type: 'terms', visible: true, order: 4, settings: { borderStyle: 'left-accent' } as TermsBlockSettings },
        { id: 'blk-bank', type: 'bankAccounts', visible: true, order: 5, settings: { showHeader: true } as BankAccountsBlockSettings },
        { id: 'blk-signature', type: 'signature', visible: false, order: 6, settings: { name: '', title: '' } as SignatureBlockSettings },
        { id: 'blk-footer', type: 'footer', visible: true, order: 7, settings: { showMersis: true, showTradeRegistry: true, showContact: true } as FooterBlockSettings },
      ],
    },
  ],
};

export const DEFAULT_TEMPLATE_CONFIG: OfferTemplateConfig = {
  id: '',
  companyId: '',
  templateName: 'Varsayılan Şablon',
  isDefault: true,
  anaRenk: '#1a3a7a',
  aksanRenk: '#4a90d9',
  font: 'Arial',
  fontBoyutu: 13,
  layoutConfig: DEFAULT_LAYOUT_CONFIG,
  createdAt: new Date().toISOString(),
};

// ============================================================================
// BLOK METAVERİSİ (editörde kullanılacak)
// ============================================================================

export const CONTENT_BLOCK_CATALOG: { type: ContentBlockType; label: string; icon: string }[] = [
  { type: 'heading', label: 'Başlık', icon: 'Title' },
  { type: 'paragraph', label: 'Metin', icon: 'Notes' },
  { type: 'quote', label: 'Alıntı', icon: 'FormatQuote' },
  { type: 'statGrid', label: 'İstatistik Grid', icon: 'GridView' },
  { type: 'serviceGrid', label: 'Hizmet Grid', icon: 'Apps' },
  { type: 'image', label: 'Görsel', icon: 'Image' },
  { type: 'divider', label: 'Ayırıcı', icon: 'HorizontalRule' },
  { type: 'spacer', label: 'Boşluk', icon: 'SpaceBar' },
];

export const OFFER_BLOCK_CATALOG: { type: OfferBlockType; label: string; icon: string }[] = [
  { type: 'greeting', label: 'Karşılama', icon: 'Waving' },
  { type: 'customerInfo', label: 'Müşteri Bilgileri', icon: 'Person' },

  { type: 'itemsTable', label: 'Ürün Tablosu', icon: 'TableChart' },
  { type: 'totals', label: 'Toplamlar', icon: 'Calculate' },
  { type: 'terms', label: 'Koşullar', icon: 'Gavel' },
  { type: 'bankAccounts', label: 'Banka Bilgileri', icon: 'AccountBalance' },
  { type: 'signature', label: 'İmza Alanı', icon: 'Draw' },
  { type: 'footer', label: 'Alt Bilgi', icon: 'VerticalAlignBottom' },
];

// ============================================================================
// YARDIMCI: Yeni blok oluştur
// ============================================================================

let blockIdCounter = 0;
export function createBlock(type: BlockType, settings?: Record<string, any>): TemplateBlock {
  blockIdCounter++;
  const defaults: Record<BlockType, Record<string, any>> = {
    heading: { text: 'Başlık', fontSize: 24, align: 'left' },
    paragraph: { text: 'Metin içeriği buraya yazılır.', fontSize: 14, align: 'left' },
    quote: { text: 'Vurgulanan metin...', fontSize: 16 },
    statGrid: { items: [{ value: '100+', label: 'Müşteri' }, { value: '10+', label: 'Yıl' }], columns: 4 },
    serviceGrid: { items: [{ title: 'Hizmet 1' }, { title: 'Hizmet 2' }], columns: 3 },
    image: { url: '', width: '100%', align: 'center' },
    divider: { color: '#e0e0e0', thickness: 1, style: 'solid' },
    spacer: { height: 30 },
    greeting: { text: 'Sunduğumuz çözüm ve hizmetlere ilişkin fiyat teklifimizi bilgilerinize sunarız.', showDate: true },
    customerInfo: { borderStyle: 'left-accent', showTaxInfo: true, showAddress: true, showPhone: true, showEmail: true },
    itemsTable: { zebraStripes: true, showDiscount: true, showSct: false },
    totals: { position: 'right', showDetails: true },
    terms: { borderStyle: 'left-accent' },
    bankAccounts: { showHeader: true },
    signature: { name: '', title: '' },
    footer: { showMersis: true, showTradeRegistry: true, showContact: true },
  };
  return {
    id: `blk-${Date.now()}-${blockIdCounter}`,
    type,
    visible: true,
    order: 0,
    settings: settings || defaults[type] || {},
  };
}

let pageIdCounter = 0;
export function createPage(type: PageType, title: string): TemplatePage {
  pageIdCounter++;
  const defaults: Record<PageType, { settings: Record<string, any>; blocks?: TemplateBlock[] }> = {
    coverPage: {
      settings: { titleText: 'Proje\nTeklifi', subtitleText: '', showDecorCircles: true, bgGradient: true, preparedBy: {} },
    },
    contentPage: {
      settings: { bgColor: '#ffffff' },
      blocks: [createBlock('heading', { text: title, fontSize: 28, align: 'left' })],
    },
    offerPage: {
      settings: { showGreeting: true },
      blocks: [
        createBlock('greeting'),
        createBlock('customerInfo'),
        createBlock('itemsTable'),
        createBlock('totals'),
        createBlock('terms'),
        createBlock('footer'),
      ],
    },
  };
  const def = defaults[type];
  return {
    id: `page-${Date.now()}-${pageIdCounter}`,
    type,
    visible: true,
    order: 0,
    title,
    settings: def.settings,
    blocks: def.blocks,
  };
}

// ============================================================================
// API MAPPING
// ============================================================================

export function mapTemplateFromApi(data: any): OfferTemplateConfig {
  let layoutConfig: TemplateLayoutConfig | undefined;
  const rawLayout = data.LayoutConfig ?? data.layoutConfig;
  if (rawLayout) {
    try {
      layoutConfig = typeof rawLayout === 'string' ? JSON.parse(rawLayout) : rawLayout;
    } catch {
      layoutConfig = undefined;
    }
  }

  return {
    id: data.Id ?? data.id ?? '',
    companyId: data.CompanyId ?? data.companyId ?? '',
    templateName: data.TemplateName ?? data.templateName ?? '',
    isDefault: data.IsDefault ?? data.isDefault ?? false,
    slogan: data.Slogan ?? data.slogan,
    logoUrl: data.LogoUrl ?? data.logoUrl,
    anaRenk: data.AnaRenk ?? data.anaRenk ?? '#1a3a7a',
    aksanRenk: data.AksanRenk ?? data.aksanRenk ?? '#4a90d9',
    font: data.Font ?? data.font ?? 'Arial',
    fontBoyutu: data.FontBoyutu ?? data.fontBoyutu ?? 13,
    odemeKosullari: data.OdemeKosullari ?? data.odemeKosullari,
    gecerlilikNotu: data.GecerlilikNotu ?? data.gecerlilikNotu,
    altBilgiNotu: data.AltBilgiNotu ?? data.altBilgiNotu,
    layoutConfig,
    createdAt: data.CreatedAt ?? data.createdAt ?? '',
    updatedAt: data.UpdatedAt ?? data.updatedAt,
  };
}

export function mapTemplateToApi(config: Partial<OfferTemplateConfig>): Record<string, any> {
  const body: Record<string, any> = {};
  if (config.id) body.Id = config.id;
  if (config.templateName !== undefined) body.TemplateName = config.templateName;
  if (config.slogan !== undefined) body.Slogan = config.slogan;
  if (config.logoUrl !== undefined) body.LogoUrl = config.logoUrl;
  if (config.anaRenk !== undefined) body.AnaRenk = config.anaRenk;
  if (config.aksanRenk !== undefined) body.AksanRenk = config.aksanRenk;
  if (config.font !== undefined) body.Font = config.font;
  if (config.fontBoyutu !== undefined) body.FontBoyutu = config.fontBoyutu;
  if (config.odemeKosullari !== undefined) body.OdemeKosullari = config.odemeKosullari;
  if (config.gecerlilikNotu !== undefined) body.GecerlilikNotu = config.gecerlilikNotu;
  if (config.altBilgiNotu !== undefined) body.AltBilgiNotu = config.altBilgiNotu;
  if (config.layoutConfig !== undefined) {
    body.LayoutConfig = JSON.stringify(config.layoutConfig);
  }
  return body;
}
